/**
 * Centralized Application Initialization Service
 * Coordinates auth verification, FolderService initialization, and URL parameter setup
 * Ensures everything is ready before the app renders
 */

import { AuthService } from './AuthService';
import { FolderService } from './FolderService';
import { persistentLogger } from '../utils/PersistentLogger';
import { STORAGE_KEYS } from '../config/directory';
import type { BookDirectory } from '../types/directory';

const INIT_COMPLETE_EVENT = 'app-initialization-complete';
const INIT_FAILED_EVENT = 'app-initialization-failed';

export interface InitResult {
  success: boolean;
  error?: string;
  folder?: string;
  urlParam?: string;
}

export class AppInitializer {
  private static initialized = false;
  private static initPromise: Promise<InitResult> | null = null;

  /**
   * Initialize the application
   * This must complete before the app renders
   */
  static async init(): Promise<InitResult> {
    // If already initialized, return cached result
    if (this.initialized && this.initPromise) {
      persistentLogger.log('AppInitializer', 'Already initialized, returning cached result');
      return this.initPromise;
    }

    // If initialization is in progress, return the same promise
    if (this.initPromise) {
      persistentLogger.log('AppInitializer', 'Initialization in progress, returning existing promise');
      return this.initPromise;
    }

    persistentLogger.log('AppInitializer', 'Starting application initialization...');

    this.initPromise = this._doInit();

    try {
      const result = await this.initPromise;
      if (result.success) {
        this.initialized = true;
        persistentLogger.log('AppInitializer', 'Initialization complete', result);
        window.dispatchEvent(new CustomEvent(INIT_COMPLETE_EVENT, { detail: result }));
      } else {
        persistentLogger.error('AppInitializer', 'Initialization failed', result);
        window.dispatchEvent(new CustomEvent(INIT_FAILED_EVENT, { detail: result }));
      }
      return result;
    } catch (error) {
      const result: InitResult = {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown initialization error'
      };
      persistentLogger.error('AppInitializer', 'Initialization exception', { error, result });
      window.dispatchEvent(new CustomEvent(INIT_FAILED_EVENT, { detail: result }));
      return result;
    }
  }

  /**
   * Perform the actual initialization steps
   */
  private static async _doInit(): Promise<InitResult> {
    // Step 1: Check if user is authenticated
    const isAuthenticated = AuthService.isAuthenticated();
    persistentLogger.log('AppInitializer', 'Step 1: Checking authentication', { 
      isAuthenticated,
      hasToken: !!AuthService.getToken()
    });
    
    if (!isAuthenticated) {
      persistentLogger.log('AppInitializer', 'User not authenticated, initialization will complete after login');
      return {
        success: true, // Still success, just not authenticated yet
        error: undefined
      };
    }

    // Step 2: Verify token is still valid (async)
    persistentLogger.log('AppInitializer', 'Step 2: Verifying token');
    try {
      const token = AuthService.getToken();
      if (token) {
        const authHeader = AuthService.getAuthHeader();
        persistentLogger.log('AppInitializer', 'Sending token verification request');
        const response = await fetch('/api/auth/verify', {
          headers: {
            'Authorization': authHeader!,
          },
        });

        persistentLogger.log('AppInitializer', 'Token verification response', { 
          ok: response.ok,
          status: response.status,
          statusText: response.statusText
        });

        if (!response.ok) {
          // Token is invalid, clear auth
          persistentLogger.error('AppInitializer', 'Token verification failed - clearing auth', {
            status: response.status
          });
          AuthService.clearAuth();
          return {
            success: true, // Still success, user will need to login
            error: undefined
          };
        }
        persistentLogger.log('AppInitializer', 'Token verification successful');
      } else {
        persistentLogger.warn('AppInitializer', 'No token found for verification');
      }
    } catch (error) {
      persistentLogger.error('AppInitializer', 'Token verification exception', { error });
      AuthService.clearAuth();
      return {
        success: true, // Still success, user will need to login
        error: undefined
      };
    }

    // Step 2.5: Initialize sync count tracking (on refresh or after token verification)
    try {
      const { productionFileSyncService } = await import('./ProductionFileSyncService');
      await productionFileSyncService.initializeCountTracking();
    } catch (error) {
      console.error('[AppInitializer] Failed to initialize sync count tracking:', error);
      // Don't fail initialization if count tracking fails
    }

    // Step 3: Initialize FolderService from URL parameter
    persistentLogger.log('AppInitializer', 'Step 3: Checking URL parameter for folder');
    
    // Read URL parameter from window.location directly (most reliable)
    const urlParams = new URLSearchParams(window.location.search);
    const booksToEdit = urlParams.get('books-to-edit');
    
    // Also check window.location.href as fallback
    const urlFromHref = new URL(window.location.href);
    const booksToEditFromHref = urlFromHref.searchParams.get('books-to-edit');
    
    persistentLogger.log('AppInitializer', 'URL parameter check', { 
      booksToEdit, 
      booksToEditFromHref,
      search: window.location.search,
      fullUrl: window.location.href,
      hash: window.location.hash
    });
    
    // Use whichever one is found
    const finalBooksToEdit = booksToEdit || booksToEditFromHref;
    
    if (finalBooksToEdit) {
      // Check if this is S3 mode ('real' or 'practice') or local mode (directory name)
      if (finalBooksToEdit === 'real' || finalBooksToEdit === 'practice') {
        // S3 Mode: URL param maps to 'real' or 'practice'
        const folder = finalBooksToEdit === 'real' ? 'books' : 'practice';
        persistentLogger.log('AppInitializer', 'S3 Mode - URL parameter found, mapping to folder', { 
          booksToEdit: finalBooksToEdit, 
          folder 
        });
        FolderService.setFolder(folder);
        persistentLogger.log('AppInitializer', 'FolderService initialized from URL (S3 mode)', { 
          booksToEdit: finalBooksToEdit, 
          folder 
        });
        return {
          success: true,
          folder,
          urlParam: finalBooksToEdit
        };
      } else {
        // Local Mode: URL param is a directory name - need to resolve to full path
        persistentLogger.log('AppInitializer', 'Local Mode - URL parameter is directory name', { 
          booksToEdit: finalBooksToEdit 
        });
        
        // Read stored directories from localStorage
        const storedDirectoriesString = localStorage.getItem(STORAGE_KEYS.BOOK_DIRECTORIES);
        if (storedDirectoriesString) {
          try {
            const storedDirectories: BookDirectory[] = JSON.parse(storedDirectoriesString);
            const directory = storedDirectories.find(d => d.name === finalBooksToEdit);
            
            if (directory) {
              // Found the directory - use its full path
              const folderPath = directory.path;
              persistentLogger.log('AppInitializer', 'Local Mode - Resolved directory name to path', { 
                directoryName: finalBooksToEdit,
                folderPath
              });
              FolderService.setFolder(folderPath);
              persistentLogger.log('AppInitializer', 'FolderService initialized from URL (Local mode)', { 
                booksToEdit: finalBooksToEdit, 
                folder: folderPath 
              });
              return {
                success: true,
                folder: folderPath,
                urlParam: finalBooksToEdit
              };
            } else {
              persistentLogger.warn('AppInitializer', 'Local Mode - Directory name not found in stored directories', { 
                booksToEdit: finalBooksToEdit,
                availableDirectories: storedDirectories.map(d => d.name)
              });
            }
          } catch (error) {
            persistentLogger.error('AppInitializer', 'Local Mode - Error parsing stored directories', { error });
          }
        } else {
          persistentLogger.warn('AppInitializer', 'Local Mode - No stored directories found in localStorage');
        }
      }
    }

    // Step 4: No URL parameter - check localStorage for stored preference
    persistentLogger.log('AppInitializer', 'Step 4: No URL parameter, checking localStorage');
    
    // First check for S3 mode preference
    const storedS3Folder = localStorage.getItem('selected-s3-folder');
    if (storedS3Folder && (storedS3Folder === 'books' || storedS3Folder === 'practice')) {
      // S3 Mode: Initialize FolderService from stored preference
      persistentLogger.log('AppInitializer', 'S3 Mode - Stored folder preference found', { storedS3Folder });
      FolderService.setFolder(storedS3Folder);
      persistentLogger.log('AppInitializer', 'FolderService initialized from localStorage (S3 mode)', { folder: storedS3Folder });
      return {
        success: true,
        folder: storedS3Folder,
        urlParam: undefined // URL param will be added by BookFileSelector
      };
    }
    
    // Then check for local mode preference
    const lastSelectedDirectoryName = localStorage.getItem(STORAGE_KEYS.LAST_SELECTED_DIRECTORY);
    if (lastSelectedDirectoryName) {
      persistentLogger.log('AppInitializer', 'Local Mode - Last selected directory name found', { lastSelectedDirectoryName });
      
      // Read stored directories to resolve name to path
      const storedDirectoriesString = localStorage.getItem(STORAGE_KEYS.BOOK_DIRECTORIES);
      if (storedDirectoriesString) {
        try {
          const storedDirectories: BookDirectory[] = JSON.parse(storedDirectoriesString);
          const directory = storedDirectories.find(d => d.name === lastSelectedDirectoryName);
          
          if (directory) {
            // Found the directory - use its full path
            const folderPath = directory.path;
            persistentLogger.log('AppInitializer', 'Local Mode - Resolved last selected directory to path', { 
              directoryName: lastSelectedDirectoryName,
              folderPath
            });
            FolderService.setFolder(folderPath);
            persistentLogger.log('AppInitializer', 'FolderService initialized from localStorage (Local mode)', { 
              folder: folderPath 
            });
            return {
              success: true,
              folder: folderPath,
              urlParam: undefined // URL param will be added by BookFileSelector
            };
          } else {
            persistentLogger.warn('AppInitializer', 'Local Mode - Last selected directory not found in stored directories', { 
              lastSelectedDirectoryName,
              availableDirectories: storedDirectories.map(d => d.name)
            });
          }
        } catch (error) {
          persistentLogger.error('AppInitializer', 'Local Mode - Error parsing stored directories', { error });
        }
      } else {
        persistentLogger.warn('AppInitializer', 'Local Mode - No stored directories found in localStorage');
      }
    } else {
      persistentLogger.log('AppInitializer', 'No stored folder preference found (neither S3 nor local)');
    }

    persistentLogger.log('AppInitializer', 'Initialization complete - no folder set yet');
    return {
      success: true,
      folder: undefined,
      urlParam: undefined
    };
  }

  /**
   * Re-initialize after login
   * Called when user successfully logs in
   * Skips token verification since we just logged in - token is fresh
   */
  static async reinitAfterLogin(): Promise<InitResult> {
    persistentLogger.log('AppInitializer', 'reinitAfterLogin() called');
    this.initialized = false;
    this.initPromise = null;
    
    // Check auth state
    const isAuthenticated = AuthService.isAuthenticated();
    const hasToken = !!AuthService.getToken();
    persistentLogger.log('AppInitializer', 'Auth state before reinit', { isAuthenticated, hasToken });
    
    // After login, we know the user is authenticated - skip verification
    // Just initialize FolderService from URL parameter
    persistentLogger.log('AppInitializer', 'Checking URL parameter after login');
    const urlParams = new URLSearchParams(window.location.search);
    const booksToEdit = urlParams.get('books-to-edit');
    persistentLogger.log('AppInitializer', 'URL parameter after login', { booksToEdit, fullUrl: window.location.href });
    
    if (booksToEdit) {
      // Check if this is S3 mode ('real' or 'practice') or local mode (directory name)
      if (booksToEdit === 'real' || booksToEdit === 'practice') {
        // S3 Mode: URL param maps to 'real' or 'practice'
        const folder = booksToEdit === 'real' ? 'books' : 'practice';
        persistentLogger.log('AppInitializer', 'S3 Mode - URL parameter found after login', { booksToEdit, folder });
        FolderService.setFolder(folder);
        persistentLogger.log('AppInitializer', 'FolderService initialized from URL after login (S3 mode)', { booksToEdit, folder });
        const result: InitResult = {
          success: true,
          folder,
          urlParam: booksToEdit
        };
        this.initialized = true;
        persistentLogger.log('AppInitializer', 'reinitAfterLogin() complete - with folder', result);
        window.dispatchEvent(new CustomEvent(INIT_COMPLETE_EVENT, { detail: result }));
        return result;
      } else {
        // Local Mode: URL param is a directory name - need to resolve to full path
        persistentLogger.log('AppInitializer', 'Local Mode - URL parameter is directory name after login', { booksToEdit });
        
        // Read stored directories from localStorage
        const storedDirectoriesString = localStorage.getItem(STORAGE_KEYS.BOOK_DIRECTORIES);
        if (storedDirectoriesString) {
          try {
            const storedDirectories: BookDirectory[] = JSON.parse(storedDirectoriesString);
            const directory = storedDirectories.find(d => d.name === booksToEdit);
            
            if (directory) {
              // Found the directory - use its full path
              const folderPath = directory.path;
              persistentLogger.log('AppInitializer', 'Local Mode - Resolved directory name to path after login', { 
                directoryName: booksToEdit,
                folderPath
              });
              FolderService.setFolder(folderPath);
              persistentLogger.log('AppInitializer', 'FolderService initialized from URL after login (Local mode)', { 
                booksToEdit, 
                folder: folderPath 
              });
              const result: InitResult = {
                success: true,
                folder: folderPath,
                urlParam: booksToEdit
              };
              this.initialized = true;
              persistentLogger.log('AppInitializer', 'reinitAfterLogin() complete - with folder', result);
              window.dispatchEvent(new CustomEvent(INIT_COMPLETE_EVENT, { detail: result }));
              return result;
            } else {
              persistentLogger.warn('AppInitializer', 'Local Mode - Directory name not found in stored directories after login', { 
                booksToEdit,
                availableDirectories: storedDirectories.map(d => d.name)
              });
            }
          } catch (error) {
            persistentLogger.error('AppInitializer', 'Local Mode - Error parsing stored directories after login', { error });
          }
        } else {
          persistentLogger.warn('AppInitializer', 'Local Mode - No stored directories found in localStorage after login');
        }
      }
    }
    
    // No URL parameter - BookFileSelector will handle adding it
    persistentLogger.log('AppInitializer', 'No URL parameter after login - BookFileSelector will add it');
    const result: InitResult = {
      success: true,
      folder: undefined,
      urlParam: undefined
    };
    this.initialized = true;
    
    // Initialize sync count tracking after login
    try {
      const { productionFileSyncService } = await import('./ProductionFileSyncService');
      await productionFileSyncService.initializeCountTracking();
    } catch (error) {
      console.error('[AppInitializer] Failed to initialize sync count tracking:', error);
    }
    
    persistentLogger.log('AppInitializer', 'reinitAfterLogin() complete - no folder yet', result);
    window.dispatchEvent(new CustomEvent(INIT_COMPLETE_EVENT, { detail: result }));
    return result;
  }

  /**
   * Check if initialization is complete
   */
  static isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Add listener for initialization complete event
   */
  static onInitComplete(callback: (result: InitResult) => void): () => void {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<InitResult>;
      callback(customEvent.detail);
    };
    window.addEventListener(INIT_COMPLETE_EVENT, handler);
    return () => {
      window.removeEventListener(INIT_COMPLETE_EVENT, handler);
    };
  }

  /**
   * Add listener for initialization failed event
   */
  static onInitFailed(callback: (result: InitResult) => void): () => void {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<InitResult>;
      callback(customEvent.detail);
    };
    window.addEventListener(INIT_FAILED_EVENT, handler);
    return () => {
      window.removeEventListener(INIT_FAILED_EVENT, handler);
    };
  }
}

